import { ProductsResponse } from "#interfaces/http-response/responses/http-responses";
import { Category } from "#interfaces/product/category.interface";
import { ProductListSelect } from "#interfaces/product/product-list-select.interface";
import { Subcategory } from "#interfaces/product/subcategory.interface";
import { CategoryService } from "#services/category/category.service";
import { ProductService } from "#services/product/product.service";
import { SubCategoryService } from "#services/sub-category/sub-category.service";
import { AlertService } from "#utils/alert-service/alert-service.service";
import {
  Component,
  Inject,
  Injectable,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { FormBuilder, FormControl, Validators } from "@angular/forms";
import { MatCheckboxChange } from "@angular/material/checkbox";
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogRef,
} from "@angular/material/dialog";
import { MatPaginatorIntl, PageEvent } from "@angular/material/paginator";
import { Observable, Subject, debounceTime, takeUntil } from "rxjs";
import { CheckSelectedDialogComponent } from "./components/check-selected-dialog/check-selected-dialog.component";
import { RoleChecker } from "#utils/role-checker/role-checker";

@Injectable()
export class MyCustomPaginatorIntl implements MatPaginatorIntl {
  changes = new Subject<void>();

  // For internationalization, the `$localize` function from
  // the `@angular/localize` package can be used.
  firstPageLabel = `Primera pagina`;
  itemsPerPageLabel = `Elementos por pagina`;
  lastPageLabel = `Ultima pagina`;

  // You can set labels to an arbitrary string too, or dynamically compute
  // it through other third-party internationalization libraries.
  nextPageLabel = "Siguiente pagina";
  previousPageLabel = "Pagina anterior";

  getRangeLabel(page: number, pageSize: number, length: number): string {
    if (length === 0) {
      return `Pagina 1 de 1`;
    }
    const amountPages = Math.ceil(length / pageSize);
    return `Pagina ${page + 1} of ${amountPages}      Elementos: ${length}`;
  }
}

@Component({
  selector: "app-products-dialog-v2",
  templateUrl: "./products-dialog-v2.component.html",
  styles: [
    `
      table {
      }

      thead {
        @apply dark:bg-card text-gray-500;
      }

      th {
        @apply p-4 font-semibold text-md border-b-[1px] border-t-[1px];
      }

      td {
        @apply p-4 font-normal border-b-[1px];
      }

      @media (max-width: 768px) {
        .mat-mdc-paginator-range-actions {
          display: none !important;
        }
      }
    `,
  ],
  providers: [{ provide: MatPaginatorIntl, useClass: MyCustomPaginatorIntl }],
  standalone: false,
})
export class ProductsDialogV2Component implements OnInit, OnDestroy {
  // Observables
  public products$: Observable<ProductsResponse> | null = null;
  public unSuscribe$ = new Subject();
  limit = 10;

  // locals

  public productData: ProductListSelect[] = [];
  public filteredProducts: ProductListSelect[] = [];

  public productsToAdd: ProductListSelect[] = [];

  public subCategories: Subcategory[] = [];
  public filteredSubCategories: Subcategory[] = [];

  public categories: Category[] = [];
  public filteredCategories: Category[] = [];

  filterForm = this.formBuilder.group({
    search: [""],
    subCategory: new FormControl<number | string>(""),
    category: new FormControl<number | string>(""),
    genericItemQuantity: new FormControl(1, [
      Validators.minLength(1),
      Validators.maxLength(2),
    ]),
  });

  pagination: {
    pageIndex: number;
    pageSize: number;
    length: number;
    previousPageIndex: number;
    currentSelected: number;
  };

  constructor(
    private productService: ProductService,
    private alertService: AlertService,
    private formBuilder: FormBuilder,
    private subCategoryService: SubCategoryService,
    private categoryService: CategoryService,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      productsSelected: ProductListSelect[];
      limitSelected: number;
    },
    private dialogRef: MatDialogRef<ProductsDialogV2Component>,
    private dialogService: MatDialog,
  ) {}

  ngOnInit(): void {
    this.loadPagination();
    this.productsToAdd = this.data.productsSelected
      ? [...this.data.productsSelected]
      : [];
    this.limit = this.data.limitSelected ?? undefined;
    this.loadProducts();
    this.searchInputValueChanges();
    this.subCategoryValueChanges();
    this.categoryValueChanges();
    this.genericItemQuantityChange();
  }

  ngOnDestroy(): void {
    this.unSuscribe$.next(null);
    this.unSuscribe$.complete();
  }

  onNoClick(): void {
    this.dialogRef.close();
  }

  requestFromService({
    pageIndex = 0,
    getAll = false,
    pageSize = 10,
    search = "",
  }) {
    this.productService
      .getListSelect(search, pageIndex, pageSize, getAll)
      .pipe(takeUntil(this.unSuscribe$))
      .subscribe({
        next: (response) => {
          this.productData = response.data ?? [];
          this.filteredProducts = this.productData;
          this.loadPagination({
            ...this.pagination,
            length: response.totalItems ?? 0,
            pageIndex: response.pageNumber ?? this.pagination.pageIndex,
          });
        },
        error: (error) => {
          this.alertService.errorAlert(
            error.error.Errors ?? "",
            error.error.Message ?? "Error al obtener los productos",
          );
        },
      });
  }

  loadProducts() {
    this.products$ = this.productService.products$;
    this.requestFromService({
      pageIndex: this.pagination.pageIndex,
      pageSize: this.pagination.pageSize,
      search: this.filterForm.getRawValue().search,
    });
  }

  loadSubCategories() {
    this.subCategoryService
      .getSubCategories(0, 10, "", "", "", true)
      .pipe(takeUntil(this.unSuscribe$))
      .subscribe({
        next: (response) => {
          this.subCategories = response.data ?? [];
          this.filteredSubCategories = this.subCategories;
        },
        error: (error) => {
          this.alertService.errorAlert(
            error.error.Errors ?? "",
            error.error.Message ?? "Error al obtener las subcategorias",
          );
        },
      });
  }

  displaySubCategory = (subCategoryId: number) => {
    if (subCategoryId === 0) {
      return "Todas las subcategorias";
    }

    return (
      this.subCategories.find((subCategory) => subCategory.id === subCategoryId)
        ?.name ?? ""
    );
  };

  loadCategories() {
    this.categoryService
      .getCategories(0, 10, "", "", "", true)
      .pipe(takeUntil(this.unSuscribe$))
      .subscribe({
        next: (response) => {
          this.categories = response.data ?? [];
          this.filteredCategories = this.categories;
        },
        error: (error) => {
          this.alertService.errorAlert(
            error.error.Errors ?? "",
            error.error.Message ?? "Error al obtener las categorias",
          );
        },
      });
  }

  displayCategory = (categoryId: number) => {
    if (categoryId === 0) {
      return "Todas las categorias";
    }

    return (
      this.categories.find((category) => category.id === categoryId)?.name ?? ""
    );
  };

  loadPagination(
    options: {
      pageIndex: number;
      pageSize: number;
      length: number;
      previousPageIndex: number;
      currentSelected: number;
    } = {
      pageIndex: 0,
      pageSize: 10,
      length: 0,
      previousPageIndex: 0,
      currentSelected: 0,
    },
  ) {
    this.pagination = { ...options };
  }

  onPageChange(event: PageEvent) {
    this.loadPagination({
      ...this.pagination,
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      length: event.length,
      previousPageIndex: event.previousPageIndex,
      currentSelected: 0,
    });
    this.requestFromService({
      pageIndex: event.pageIndex,
      pageSize: event.pageSize,
      search: this.filterForm.getRawValue().search,
    });
  }

  selectTopTenProducts(event: MatCheckboxChange) {
    if (event.checked) {
      this.productsToAdd = this.filteredProducts.slice(0, 10);
    } else {
      this.productsToAdd = [];
    }
  }

  AllSelectedProductsIsTrue() {
    if (this.productsToAdd.length === 0) {
      return false;
    }

    if (this.productsToAdd.length !== this.filteredProducts.length) {
      return false;
    }

    const sortedProductsToAdd = this.productsToAdd
      .slice()
      .sort((a, b) => a.id - b.id);
    const sortedFilteredProducts = this.filteredProducts
      .slice()
      .sort((a, b) => a.id - b.id);

    for (let i = 0; i < sortedProductsToAdd.length; i++) {
      if (sortedProductsToAdd[i].id !== sortedFilteredProducts[i].id) {
        return false;
      }
    }

    return true;
  }

  addProductOnCheck(product: ProductListSelect, event: MatCheckboxChange) {
    if (!this.checkProductValidityWithoutAlert() && event.checked) {
      event.source.checked = false;
      return;
    }

    if (event.checked && !this.productExistsWithoutAlert(product)) {
      this.addProduct(product, event);
      this.pagination.currentSelected += 1;
    } else {
      this.pagination.currentSelected -= 1;
      this.deleteProductInSelection(product);
    }
  }

  addProduct(product: ProductListSelect, event: any) {
    if (!this.checkMaxProductValidity()) {
      return;
    }

    if (this.productExistsWithAlert(product)) {
      return;
    }

    this.productsToAdd.push(product);
  }

  checkIfProductToAddisGeneric(product: ProductListSelect) {
    if (product.code === "ART999999 ") {
    }
  }

  checkMaxProductValidity() {
    if (this.limit && this.productsToAdd?.length === this.limit) {
      this.alertService.errorAlert(
        [],
        `Solo puede agregar un maximo de ${this.limit} productos`,
      );
      return false;
    }
    return true;
  }

  checkProductValidityWithoutAlert() {
    if (this.limit && this.productsToAdd.length === this.limit) {
      return false;
    }
    return true;
  }

  productExistsWithAlert(product: ProductListSelect) {
    if (
      this.productsToAdd.some(
        (selectedProduct) => selectedProduct.id === product.id,
      )
    ) {
      this.alertService.errorAlert([], "El producto ya ha sido agregado");
      return true;
    }

    return false;
  }

  productExistsWithoutAlert(product: ProductListSelect) {
    const productExists = this.productsToAdd.some(
      (selectedProduct) => selectedProduct.id === product.id,
    );

    return productExists;
  }

  deleteProductInSelection(product: ProductListSelect) {
    this.productsToAdd = this.productsToAdd.filter(
      (selectedProduct) => selectedProduct.id !== product.id,
    );
  }

  searchInputValueChanges() {
    this.filterForm.controls.search.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unSuscribe$))
      .subscribe({
        next: (value) => {
          this.requestFromService({
            pageIndex: 0,
            pageSize: this.pagination.pageSize,
            search: value,
          });
        },
      });
  }

  subCategoryValueChanges() {
    this.filterForm.controls.subCategory.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unSuscribe$))
      .subscribe({
        next: (value) => {
          this.filterSubCategories(value);

          if (typeof value === "string") {
            return;
          }
        },
      });
  }

  categoryValueChanges() {
    this.filterForm.controls.category.valueChanges
      .pipe(debounceTime(300), takeUntil(this.unSuscribe$))
      .subscribe({
        next: (value) => {
          this.filterCategories(value);

          if (typeof value === "string") {
            return;
          }
        },
      });
  }

  filterSubCategories(value: number | string) {
    if (typeof value === "string") {
      this.filteredSubCategories = this.subCategories.filter((subCategory) =>
        subCategory.name.toLowerCase().includes(value.toLowerCase()),
      );
      return;
    }

    if (typeof value === "number") {
      this.filteredSubCategories = this.subCategories;
      return;
    }

    if (!value) {
      this.filteredSubCategories = this.subCategories;
      return;
    }
  }

  filterCategories(value: number | string) {
    if (typeof value === "string") {
      this.filteredCategories = this.categories.filter((category) =>
        category.name.toLowerCase().includes(value.toLowerCase()),
      );
      return;
    }

    if (typeof value === "number") {
      this.filteredCategories = this.categories;
      return;
    }

    if (!value) {
      this.filteredCategories = this.categories;
      return;
    }
  }

  closeModalWithData() {
    this.dialogRef.close(this.productsToAdd);
  }

  checkProductsSelected() {
    const dialogRef = this.dialogService.open(CheckSelectedDialogComponent, {
      width: "80%",
      maxWidth: "950px",
      height: "80vh",
      data: {
        products: this.productsToAdd,
      },
    });

    dialogRef.afterClosed().subscribe((products) => {
      if (products) {
        this.productsToAdd = products;
      }
    });
  }

  genericItemQuantityChange() {
    this.filterForm.controls.genericItemQuantity.valueChanges
      .pipe(takeUntil(this.unSuscribe$))
      .subscribe({
        next: (value) => {
          if (!value) {
            this.filterForm.controls.genericItemQuantity.setValue(1);
          }

          this.filterForm.controls.genericItemQuantity.setValidators([
            Validators.minLength(1),
            Validators.maxLength(this.limit - this.productsToAdd.length),
          ]);
        },
      });
  }

  get canViewInventory(): boolean {
    return (
      RoleChecker("SuperAdmin") ||
      RoleChecker("Admin") ||
      RoleChecker("Manager")
    );
  }
}
